import {Injectable} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {BehaviorSubject, catchError, filter, Observable, switchMap, take, tap, throwError} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {get} from 'lodash';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {GraphNodeMapListKey} from '../api/interfaces/graph-node.interface';
import {isInIframe} from '../shared/helpers/maporium.validators';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {

  constructor(private router: Router,
              private activateRoute: ActivatedRoute,
              private userService: UserService,
              private mapService: MapService,
              private matSnackBar: MatSnackBar,
              private matDialog: MatDialog,
              private http: HttpClient) {}

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private isHandling403 = false;
  private isNavigatingFromInterceptor = false;

  private safeNavigate(commands: any[], extras?: any): void {
    if (this.isNavigatingFromInterceptor) return;
    const target = '/' + commands.join('/').replace(/^\/+/, '');
    if (this.router.url === target) return;
    this.isNavigatingFromInterceptor = true;
    this.router.navigate(commands, extras).then(
      () => this.isNavigatingFromInterceptor = false,
      () => this.isNavigatingFromInterceptor = false
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = localStorage.getItem('accessToken');

    // Don't attach our bearer to third-party URLs (e.g. S3 presigned upload
    // URLs) — extra headers invalidate the signature and the upload 403s.
    const isOurApi = request.url.startsWith(environment.apiUrl);

    if (accessToken && isOurApi) {
      request = this.addToken(request, accessToken);
    }

    if (!isOurApi) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // In iframe/smaplet mode, never redirect — just propagate errors silently
        if (isInIframe()) {
          return throwError(error);
        }

        // Redirect to inactive page if account status is inactive or closed
        const statusMsg = get(error, 'error.error.message') || get(error, 'error.message') || get(error, 'message');
        if (error.status === 403 && (statusMsg === 'inactive' || statusMsg === 'closed')) {
          console.error('User account is inactive or closed:', statusMsg);
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          // Store inactive status to prevent other navigations
          sessionStorage.setItem('userInactive', 'true');
          // Clear any existing navigation and force inactive page
          this.safeNavigate(['/inactive'], {queryParams: {status: statusMsg}});
          return throwError(error);
        }

        if (error.status === 403) {
          // Don't redirect to dashboard if user is already marked as inactive
          if (sessionStorage.getItem('userInactive') === 'true') {
            return throwError(error);
          }

          // Detect BlockDefaulterGuard's "limited" message — show full limited-mode UX
          const limitedMsg = get(error, 'error.message') || '';
          if (limitedMsg.includes('current plan does not allow')) {
            this.userService.refreshLocalUser().subscribe(() => {
              import('../dialogs/user-account-dialog.component').then(({UserAccountDialogComponent}) => {
                this.matDialog.open(UserAccountDialogComponent, {
                  width: '50%',
                  autoFocus: false,
                });
              });
            });
            return throwError(error);
          }

          // Guard against infinite loop: findAllForUser can itself 403,
          // which would re-enter this handler endlessly
          if (this.isHandling403) {
            return throwError(error);
          }
          this.isHandling403 = true;

          this.matSnackBar.open(get(error, 'error.message'), 'Close', { duration: 3500 });
          this.mapService.findAllForUser(this.userService.getCurrentUserFromStorage().id)
            .subscribe({
              next: (maps) => {
                this.isHandling403 = false;
                const allowedMap = maps.filter((e) => e.owner.id === this.userService.getCurrentUserFromStorage().id);
                if (allowedMap.length === 0) {
                  this.mapService.setCurrentSelectedMapToStore(undefined);
                  localStorage.removeItem(GraphNodeMapListKey);
                  this.safeNavigate(['/dashboard']);
                  return;
                }
                this.safeNavigate(['/dashboard/map/' + allowedMap[0].id]);
              },
              error: () => {
                this.isHandling403 = false;
                this.safeNavigate(['/dashboard']);
              }
            });
          return throwError(error);
        }
        // Special case: missing or expired subscription package — fallback to dashboard
        if (error.status === 401 && statusMsg === 'You do not have the required package or your package has expired.') {
          this.safeNavigate(['/dashboard']);

          return throwError(error);
        }
        // Token refresh flow for other 401 errors
        if (error.status === 401 && !request.url.includes('/auth/refresh-token') && statusMsg !== 'You do not have the required package or your package has expired.') {
          if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap(() => next.handle(this.addToken(request, localStorage.getItem('accessToken') || '')))
            );
          }
          this.isRefreshing = true;
          this.refreshTokenSubject.next(null);
          return this.refreshToken().pipe(
            switchMap(({ accessToken }) => {
              localStorage.setItem('accessToken', accessToken);
              this.isRefreshing = false;
              this.refreshTokenSubject.next(accessToken);
              return next.handle(this.addToken(request, accessToken));
            }),
            catchError((err) => {
              this.isRefreshing = false;
              localStorage.removeItem('accessToken');
              const mapId = this.activateRoute.snapshot.queryParams['mapId'];
              this.safeNavigate(['/signin'], mapId ? {queryParams: {mapId}} : undefined);
              return throwError(err);
            })
          );
        } else {
          if (statusMsg === 'You do not have the required package or your package has expired.') {
            this.safeNavigate(['/dashboard']);
          }
          return throwError(error);
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return throwError('No refresh token');
    }
    return this.http.post<{accessToken: string}>( environment.apiUrl + '/auth/refresh-token', { refreshToken }).pipe(
      tap(({ accessToken }) => {
        localStorage.setItem('accessToken', accessToken);
      })
    );
  }
}


